import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, doc, getDoc, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { COLLECTIONS } from "../../../../services/collections";
import { useTenant } from "../../../../hooks/useTenant";
import { toISODate } from "../../../../utils/date";
import { getMonthKey, shiftMonth, buildTrailingMonths, buildYearMonths } from "../utils/dateHelpers";
import {
    parseFinancialData,
    parseSalesData,
    parseMembersData,
    parseOccupancyData,
    parseAttendanceData,
    buildFinancialChartData,
    buildRenewalChartData,
    buildGrowthChartData,
    buildOccupancyHistory,
    buildSalesChartData,
    buildAttendanceChartData
} from "../utils/dashboardParsers";

function safeData(snapshot) {
    return snapshot?.exists() ? (snapshot.data() || {}) : {};
}

const initialMetricsState = {
    selectedDay: {},
    selectedMonth: {},
    previousMonth: {},
    snapshot: {},
    previousSnapshot: {},
    live: {},
    goals: null,
    financialChartData: [],
    renewalChartData: [],
    growthChartData: [],
    occupancyHistory: [],
    attendanceChartData: [],
};

export default function useManagerDashboard() {
    const { idTenant } = useTenant();
    const today = useMemo(() => toISODate(new Date()), []);

    const [selectedDate, setSelectedDate] = useState(today);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metricsState, setMetricsState] = useState(initialMetricsState);
    const isToday = selectedDate === today;
    const selectedMonthISO = getMonthKey(selectedDate);
    const previousDateISO = useMemo(() => shiftMonth(selectedDate, -1), [selectedDate]);
    const previousMonthISO = getMonthKey(previousDateISO);
    const selectedYear = Number(selectedDate.slice(0, 4));

    useEffect(() => {
        if (!idTenant || !isToday) {
            return undefined;
        }
        // A lógica de busca em tempo real (onSnapshot) foi removida para evitar
        // alto custo de leitura e problemas de performance com grandes bases de dados.
        // O Dashboard passará a obter esses números estritamente da collection de 'metrics' agregada.
    }, [idTenant, isToday]);

    const loadMetrics = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setError(null);

        try {
            const metricsRef = collection(db, "tenants", idTenant, COLLECTIONS.METRICS);
            const trailingMonths = buildTrailingMonths(selectedDate);
            const currentYearMonths = buildYearMonths(selectedYear);
            const previousYearMonths = buildYearMonths(selectedYear - 1);

            const uniqueMonthIds = Array.from(new Set([
                ...trailingMonths.map((item) => item.monthISO),
                ...currentYearMonths.map((item) => item.monthISO),
                ...previousYearMonths.map((item) => item.monthISO),
                selectedMonthISO,
                previousMonthISO,
            ]));

            const goalsDocId = `gerente_${selectedMonthISO}`;
            const goalsRef = doc(db, "tenants", idTenant, "performanceGoals", goalsDocId);

            // Contar contratos vencendo no mês atual e no anterior
            const expirationsQuery = query(
                collection(db, "tenants", idTenant, "memberMemberships"),
                where("endDate", ">=", `${selectedMonthISO}-01`),
                where("endDate", "<=", `${selectedMonthISO}-31`)
            );
            const previousExpirationsQuery = query(
                collection(db, "tenants", idTenant, "memberMemberships"),
                where("endDate", ">=", `${previousMonthISO}-01`),
                where("endDate", "<=", `${previousMonthISO}-31`)
            );

            // ── Busca do daily proporcional do mês anterior (para comparação MTD) ──
            // Busca daily_YYYY-MM-DD do mesmo dia no mês passado para comparar o acumulado proporcional
            const previousDailyMTDRef = doc(metricsRef, `daily_${previousDateISO}`);

            const [
                selectedDailySnap,
                previousDailySnap,
                liveSnap,
                monthlySnaps,
                goalsSnap,
                expirationsSnap,
                previousExpirationsSnap,
                previousDailyMTDSnap,
            ] = await Promise.all([
                getDoc(doc(metricsRef, `daily_${selectedDate}`)),
                getDoc(doc(metricsRef, `daily_${previousDateISO}`)),
                isToday ? getDoc(doc(metricsRef, "live")) : Promise.resolve(null),
                Promise.all(uniqueMonthIds.map((monthISO) => getDoc(doc(metricsRef, `monthly_${monthISO}`)))),
                getDoc(goalsRef),
                getCountFromServer(expirationsQuery),
                getCountFromServer(previousExpirationsQuery),
                getDoc(previousDailyMTDRef),
            ]);

            const goals = goalsSnap.exists() ? goalsSnap.data().goals || null : null;
            const expirationsMonth = expirationsSnap.data().count;
            const previousExpirationsMonth = previousExpirationsSnap.data().count;

            const monthlyMap = uniqueMonthIds.reduce((acc, monthISO, index) => {
                acc[monthISO] = safeData(monthlySnaps[index]);
                return acc;
            }, {});

            const selectedDaily = safeData(selectedDailySnap);
            const previousDaily = safeData(previousDailySnap);
            const selectedMonthly = monthlyMap[selectedMonthISO] || {};
            const previousMonthly = monthlyMap[previousMonthISO] || {};
            const liveData = isToday ? safeData(liveSnap) : {};

            const selectedDay = selectedDaily.day || {};
            const snapshot = selectedDaily.snapshot || selectedMonthly.snapshot || {};
            const previousSnapshot = previousDaily.snapshot || previousMonthly.snapshot || {};

            // ── Dados acumulados do mês (MTD) ──
            // Hoje: usa monthly (acumulado em tempo real pelos triggers)
            // Dia passado: usa daily.mtd (foto acumulada até aquele dia pelo CRON)
            const selectedMonthData = isToday
                ? selectedMonthly
                : (selectedDaily.mtd || selectedMonthly);

            // ── Dados do mês anterior para comparação proporcional ──
            // Sempre busca o MTD proporcional do mês anterior (se não existir, cai para o mês total)
            const previousDailyMTDData = previousDailyMTDSnap ? safeData(previousDailyMTDSnap) : {};
            const previousMonthData = previousDailyMTDData.mtd || previousMonthly;

            setMetricsState({
                selectedDay,
                selectedMonth: selectedMonthData || {},
                previousMonth: previousMonthData || {},
                snapshot,
                previousSnapshot: previousDailyMTDData.snapshot || previousSnapshot,
                live: liveData || {},
                goals,
                expirationsMonth,
                previousExpirationsMonth,
                financialChartData: buildFinancialChartData(trailingMonths, monthlyMap, selectedMonthISO, selectedMonthData),
                renewalChartData: buildRenewalChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData),
                growthChartData: buildGrowthChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData),
                occupancyHistory: buildOccupancyHistory(currentYearMonths, previousYearMonths, monthlyMap),
                salesChartData: buildSalesChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData),
                attendanceChartData: buildAttendanceChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData),
            });
        } catch (err) {
            console.error("[useManagerDashboard] Erro ao carregar métricas:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [idTenant, isToday, previousDateISO, previousMonthISO, selectedDate, selectedMonthISO, selectedYear]);

    useEffect(() => {
        loadMetrics();
    }, [loadMetrics]);

    const dashboardData = useMemo(() => {
        return {
            ...parseFinancialData(metricsState),
            ...parseSalesData(metricsState),
            ...parseMembersData(metricsState, isToday),
            ...parseOccupancyData(metricsState, isToday),
            ...parseAttendanceData(metricsState),
            attendanceChartData: metricsState.attendanceChartData || [],
            goals: metricsState.goals || null,
            expirationsMonth: metricsState.expirationsMonth || 0,
            previousExpirationsMonth: metricsState.previousExpirationsMonth || 0,
        };
    }, [isToday, metricsState]);

    return {
        loading,
        error,
        data: dashboardData,
        refresh: loadMetrics,
        selectedDate,
        setSelectedDate,
        isToday,
    };
}
